import { useState, useMemo, useEffect, memo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { PackageOpen, Download, ChevronDown, User, Search, ListFilter, ArrowUpAZ, ArrowDown01, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { downloadOrderSummaryExcel } from "@/utils/excelUtils";
import { motion, AnimatePresence } from "framer-motion";

interface OrderSummaryItem {
  name: string;
  quantity: number;
  price: number;
  buyers: Record<string, number>;
}

interface TotalOrderSummaryProps {
  summary: OrderSummaryItem[];
  titleStyle: string;
}

type OrderSummarySortOption = 'name-asc' | 'quantity-desc';

const sortOptions: { value: OrderSummarySortOption; label: string; icon: typeof ArrowUpAZ }[] = [
  { value: 'name-asc', label: '상품명 가나다순', icon: ArrowUpAZ },
  { value: 'quantity-desc', label: '주문수량 많은 순', icon: ArrowDown01 },
];

function TotalOrderSummaryImpl({ summary, titleStyle }: TotalOrderSummaryProps) {
  const [expandedName, setExpandedName] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState<OrderSummarySortOption>('name-asc');

  const filteredAndSortedSummary = useMemo(() => {
    let result = summary;
    if (searchTerm.trim()) {
      result = result.filter(item =>
        item.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
    }
    if (sortOption === 'name-asc') {
      result = [...result].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    } else if (sortOption === 'quantity-desc') {
      result = [...result].sort((a, b) => b.quantity - a.quantity);
    }
    return result;
  }, [summary, searchTerm, sortOption]);

  useEffect(() => {
    setExpandedName(null);
  }, [searchTerm, sortOption]);

  const handleDownload = () => {
    const sorted = [...summary];
    if (sortOption === 'name-asc') {
      sorted.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    } else if (sortOption === 'quantity-desc') {
      sorted.sort((a, b) => b.quantity - a.quantity);
    }
    downloadOrderSummaryExcel(sorted);
  };

  const toggleExpand = (name: string) => {
    setExpandedName(prev => (prev === name ? null : name));
  };

  if (summary.length === 0) {
    return (
        <Card className="shadow-xl mt-6">
            <CardHeader>
                <CardTitle className={`${titleStyle} flex items-center`}>
                <PackageOpen className="w-5 h-5 mr-2 text-indigo-600" />
                총 주문 내역
                </CardTitle>
                <CardDescription>
                    '입금 완료' 또는 '출고 완료' 상태의 주문이 없습니다.
                </CardDescription>
            </CardHeader>
        </Card>
    );
  }

  const currentSort = sortOptions.find(o => o.value === sortOption)!;

  return (
    <Card className="shadow-xl mt-6">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className={`${titleStyle} flex items-center`}>
            <PackageOpen className="w-5 h-5 mr-2 text-indigo-600" />
            총 주문 내역
          </CardTitle>
          <CardDescription className="space-y-0.5 mt-1">
            <span className="block">'입금 완료' 및 '출고 완료' 상태인 주문의 상품별 총 개수입니다. (닉네임이 '고객 닉네임'인 주문은 제외됩니다.)</span>
            <span className="block text-xs text-muted-foreground/70">'미지정' 상태 주문까지 확인하려면 우측 상단의 일괄 처리 모드를 이용하세요.</span>
          </CardDescription>
        </div>
        <Button onClick={handleDownload} size="sm" variant="outline">
          <Download className="w-4 h-4 mr-2" />
          엑셀 다운로드
        </Button>
      </CardHeader>
      <CardContent>
        {/* 검색 + 정렬 툴바 */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="상품명 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-8 w-full text-sm h-9"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                aria-label="검색어 지우기"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-gray-200 transition-colors"
              >
                <X className="h-3.5 w-3.5 text-gray-400" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 justify-end">
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-9">
                  <ListFilter className="w-4 h-4 mr-2" />
                  {currentSort.label}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {sortOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <DropdownMenuItem
                      key={option.value}
                      onClick={() => setSortOption(option.value)}
                      disabled={sortOption === option.value}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {option.label}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* 상품 목록 */}
        {filteredAndSortedSummary.length > 0 ? (
          <div className="space-y-2">
            {filteredAndSortedSummary.map((item) => {
              const isExpanded = expandedName === item.name;
              const buyerEntries = Object.entries(item.buyers).sort((a, b) => b[1] - a[1]);
              const buyerCount = buyerEntries.length;

              return (
                <div key={item.name} className="rounded-lg bg-gray-50 transition-colors">
                  <button
                    onClick={() => toggleExpand(item.name)}
                    className="flex justify-between items-center w-full p-3 text-left hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                      />
                      <span className="font-medium text-gray-800 text-base">{item.name}</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-gray-600 text-sm">{item.price.toLocaleString()}원</span>
                      <span className="font-bold text-indigo-600 text-lg">{item.quantity}개</span>
                    </div>
                  </button>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-3">
                          <div className="bg-white rounded-md border border-gray-200 p-3">
                            <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                              <User className="w-3.5 h-3.5" />
                              <span>구매자 ({buyerCount}명)</span>
                            </div>
                            <div className="max-h-48 overflow-y-auto space-y-1">
                              {buyerEntries.map(([nickname, qty]) => (
                                <div key={nickname} className="flex justify-between items-center text-sm py-1 px-2 rounded hover:bg-gray-50">
                                  <span className="text-gray-700">{nickname}</span>
                                  <span className="text-gray-500 font-medium">{qty}개</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-xs text-gray-500 text-center py-4 px-3 border-2 border-dashed border-gray-200 rounded-md bg-gray-50/30">
            <Search className="w-5 h-5 mx-auto mb-1.5 text-gray-400" />
            <p className="font-medium">'{searchTerm}'에 대한 결과가 없습니다.</p>
            <p className="mt-0.5">다른 검색어로 시도해보세요.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const TotalOrderSummary = memo(TotalOrderSummaryImpl);
